// src/components/ui/Select.jsx
// SECOND ITERATION (this session). The first version fixed a real, confirmed
// bug (white background / unreadable text in the OPEN list of a native
// <select>) by styling native <option> elements directly — that part worked,
// but it also used invented colors (#0f172a/#f1f5f9) instead of checking this
// app's REAL shared palette (shared.js) first. Both issues fixed here.
//
// BIGGER CHANGE: this is no longer a native <select> at all. The owner's real
// ask was to match TickerSearchInput.jsx's actual "feeling" — rounded corners,
// box-shadow, per-row hover highlight. A native <select>'s open list is
// rendered by the OS/browser; modern browsers only allow background-color/
// color on individual <option> elements, nothing else — no border-radius, no
// shadow, no hover-state color change, ever. That's a browser platform limit,
// not something more CSS can work around. TickerSearchInput.jsx's own
// "feeling" comes from NOT being a native control at all — it's a plain
// positioned <div> full of <button> rows it fully controls. This component
// now does the same thing: a button showing the current value, and on click,
// a custom absolutely-positioned list — same background/border/shadow/hover
// pattern as TickerSearchInput.jsx's own suggestion list (confirmed against
// the real file, not guessed), so the two feel like one design system.
//
// Colors below are lifted directly from shared.js's own `s.input`/`modal`
// values (rgba(15,30,60,0.6) background, rgba(0,212,255,0.2) border, #f8fafc
// text) and from TickerSearchInput.jsx's own suggestion-list styling
// (rgba(8,18,40,0.98) list background, rgba(0,212,255,0.06) row hover) — not
// a third invented palette.
//
// API-COMPATIBLE with the first version and with every existing call site
// (CongressPage.jsx's Tab 1/2/3 filters) — onChange still receives a
// { target: { value } } shaped object, so `e.target.value` keeps working
// unchanged. No caller needs to be touched when swapping this file in.

import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export default function Select({ value, onChange, options, placeholder, style }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const normalized = options.map((opt) => (typeof opt === 'string' ? { value: opt, label: opt } : opt));
  const current = normalized.find((o) => o.value === value);
  const displayLabel = current ? current.label : (placeholder || '');

  // Mimics a native change event's shape -- keeps every existing
  // `onChange={e => setX(e.target.value)}` call site working unchanged.
  const pick = (val) => {
    setOpen(false);
    onChange({ target: { value: val } });
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', flex: '0 0 auto', ...style }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
          width: '100%', minWidth: '140px', padding: '0.55rem 0.75rem',
          background: 'rgba(15,30,60,0.6)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '8px',
          color: current ? '#f8fafc' : '#64748b', fontSize: '0.82rem', fontFamily: 'inherit',
          cursor: 'pointer', boxSizing: 'border-box',
          // DEFENSIVE FIX (this session — real, targeted candidate, not a
          // guess): Windows Forced Colors Mode (High Contrast) overrides
          // author-specified colors on native interactive elements like
          // <button>, regardless of inline styles, UNLESS forced-color-adjust
          // is explicitly set to 'none'. This matches the reported symptom
          // (some rows keep their intended color, others get overridden) as
          // well as anything short of a live DevTools inspection can confirm.
          // Zero downside if this isn't the actual cause — this property does
          // nothing in a normal color scheme.
          forcedColorAdjust: 'none',
        }}
      >
        {displayLabel}
        <ChevronDown size={14} style={{ opacity: 0.6, flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
          background: 'rgba(8,18,40,0.98)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: '8px',
          boxShadow: '0 12px 30px rgba(0,0,0,0.5)', maxHeight: '280px', overflowY: 'auto',
          forcedColorAdjust: 'none',
        }}>
          {placeholder !== undefined && (
            <button
              onClick={() => pick('')}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,212,255,0.06)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
              style={{
                display: 'block', width: '100%', padding: '0.55rem 0.9rem', background: 'none', border: 'none',
                borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer', textAlign: 'left',
                fontFamily: 'inherit', fontSize: '0.8rem', color: '#64748b', forcedColorAdjust: 'none',
              }}
            >
              {placeholder}
            </button>
          )}
          {normalized.map((opt) => (
            <button
              key={opt.value}
              onClick={() => pick(opt.value)}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,212,255,0.06)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = value === opt.value ? 'rgba(0,212,255,0.1)' : 'none'; }}
              style={{
                display: 'block', width: '100%', padding: '0.55rem 0.9rem',
                background: value === opt.value ? 'rgba(0,212,255,0.1)' : 'none',
                border: 'none', borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer',
                textAlign: 'left', fontFamily: 'inherit', fontSize: '0.82rem', color: '#f8fafc',
                forcedColorAdjust: 'none',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
